/**
 * Invoice detail — `GET payments/invoices/{id}/`.
 *
 * "Record payment" calls `record_actual_payment`, the ONLY write in this
 * module that settles a balance (ledger CREDIT + invoice recompute via
 * `AccountService.recordPayment`). It is gated to LANDLORD/AGENT — NOT the
 * broader "manager" set: `IsLandlordOrAgent` replaces the viewset's default
 * `IsManagerOrReadOnly` permission on this one action, so a SUPERAGENT (who
 * passes every other manager check in this module) gets 403 here
 * (payments/routes.ts). A TENANT never sees the action at all — read-only.
 *
 * Edit/Delete use the plain invoice CRUD and are open to every manager role
 * including SUPERAGENT, matching `assertManagerRole`.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../auth/AuthContext';
import { parseApiError } from '../../lib/apiError';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { deleteInvoice, getInvoice, recordActualPayment } from '../../api/endpoints/payments';
import { generateQr, initiateStkPush, queryStkStatus } from '../../api/endpoints/mpesa';
import { PAYMENT_METHODS, type PaymentMethod } from '../../types/payment';

const INITIAL_PAYMENT_FORM = {
  amount: '',
  method: 'CASH' as PaymentMethod,
  reference: '',
};

/** Matches Phase 4c2's messaging-thread polling precedent (ConversationDetailPage.tsx). */
const STK_POLL_INTERVAL_MS = 8_000;

export function InvoiceDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const invoiceId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const canManage = user?.role === 'LANDLORD' || user?.role === 'AGENT' || user?.role === 'SUPERAGENT';
  const canRecordPayment = user?.role === 'LANDLORD' || user?.role === 'AGENT';

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState(INITIAL_PAYMENT_FORM);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const [mpesaTab, setMpesaTab] = useState<'STK' | 'QR'>('STK');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [checkoutRequestId, setCheckoutRequestId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => getInvoice(invoiceId),
    enabled: Number.isFinite(invoiceId),
  });

  const remove = useMutation({
    mutationFn: () => deleteInvoice(invoiceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate('..');
    },
  });

  const recordPayment = useMutation({
    mutationFn: () =>
      recordActualPayment(invoiceId, {
        amount: paymentForm.amount,
        method: paymentForm.method,
        reference: paymentForm.reference,
      }),
    onSuccess: () => {
      setPaymentForm(INITIAL_PAYMENT_FORM);
      setPaymentError(null);
      void queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (caught) => setPaymentError(parseApiError(caught).message),
  });

  function handleRecordPayment(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setPaymentError(null);
    recordPayment.mutate();
  }

  const stkPush = useMutation({
    mutationFn: () => initiateStkPush({ invoice_id: invoiceId, phone_number: phoneNumber }),
    onSuccess: (data) => setCheckoutRequestId(data.CheckoutRequestID),
  });

  // `stk-query/` is what we poll — a push does not itself confirm payment.
  // Stops polling once the status leaves PENDING (query.state.data undefined
  // means "first request in flight", which must still poll).
  const stkStatus = useQuery({
    queryKey: ['mpesa-stk-status', checkoutRequestId],
    queryFn: () => queryStkStatus({ checkout_request_id: checkoutRequestId as string }),
    enabled: checkoutRequestId !== null,
    refetchInterval: (query) =>
      query.state.data === undefined || query.state.data.status === 'PENDING'
        ? STK_POLL_INTERVAL_MS
        : false,
  });

  useEffect(() => {
    if (stkStatus.data?.status === 'SUCCESS') {
      void queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
    }
  }, [stkStatus.data?.status, invoiceId, queryClient]);

  function handleStkSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setCheckoutRequestId(null);
    stkPush.mutate();
  }

  const qrGeneration = useMutation({
    mutationFn: () => generateQr({ invoice_id: invoiceId }),
  });

  if (!Number.isFinite(invoiceId)) {
    return <p>Invalid invoice id.</p>;
  }

  if (query.isPending) return <p>Loading…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(query.error).message}
      </p>
    );
  }

  const invoice = query.data;

  /**
   * Client-side PDF, matching how the Flutter app produced its own invoice
   * download (`invoice_service.dart`'s `pdf`+`printing` packages, a
   * different library, same pure-client approach — no backend render
   * endpoint exists). Layout is ported conceptually from that same Flutter
   * service (header/BILL TO block/line-item table/total/status), via the
   * shared `lib/pdf.ts` chrome module the other 3 report exports also use.
   *
   * Shown to everyone who can already see this invoice, tenants included: it
   * only reformats data the page is already displaying.
   *
   * `jspdf` (and `lib/pdf.ts`, which only imports jsPDF's TYPE) are
   * dynamically imported — jspdf pulls in `html2canvas` (~200KB), which
   * every other page would otherwise pay for on initial load just for this
   * one button on one detail page.
   */
  async function handlePrintInvoice(): Promise<void> {
    const { jsPDF } = await import('jspdf');
    const { createBrandedDocument, drawTable, downloadPdf, formatPdfMoney, formatPdfDate, PAGE_MARGIN } =
      await import('../../lib/pdf');

    const { doc, contentStartY } = createBrandedDocument(jsPDF, 'RENTAL INVOICE');
    let y = contentStartY;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`Invoice #: ${invoice.invoice_number}`, PAGE_MARGIN, y);
    doc.text(`Due: ${formatPdfDate(invoice.due_date)}`, doc.internal.pageSize.getWidth() - PAGE_MARGIN, y, {
      align: 'right',
    });
    y += 10;

    doc.setFont('helvetica', 'bold');
    doc.text('BILL TO:', PAGE_MARGIN, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.text(invoice.tenant_name, PAGE_MARGIN, y);
    y += 5;
    doc.text(`${invoice.unit_number}, ${invoice.property_name}`, PAGE_MARGIN, y);
    y += 10;

    y = drawTable(doc, {
      startY: y,
      head: ['Description', 'Amount'],
      rows: [
        [invoice.charge_type, formatPdfMoney(invoice.amount_due)],
        ['Amount paid', formatPdfMoney(invoice.amount_paid)],
      ],
    });
    y += 10;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(
      `TOTAL: ${formatPdfMoney(invoice.balance)}`,
      doc.internal.pageSize.getWidth() - PAGE_MARGIN,
      y,
      { align: 'right' },
    );
    y += 10;

    doc.setFontSize(11);
    doc.setTextColor(invoice.status === 'PAID' ? '#059669' : '#dc2626');
    doc.text(`Status: ${invoice.status}`, PAGE_MARGIN, y);
    doc.setTextColor('#000000');
    doc.setFont('helvetica', 'normal');

    downloadPdf(doc, `invoice-${invoice.invoice_number}.pdf`);
  }

  return (
    <section>
      <h2>Invoice {invoice.invoice_number}</h2>

      <p>
        <button type="button" onClick={() => void handlePrintInvoice()}>
          Print invoice
        </button>
      </p>

      {canManage && (
        <p>
          <Link to="edit">Edit</Link>{' '}
          <button type="button" className="button--danger" onClick={() => setConfirmDeleteOpen(true)}>
            Delete
          </button>
        </p>
      )}

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete invoice"
        message={`Delete invoice "${invoice.invoice_number}"? This cannot be undone.`}
        tone="danger"
        confirmLabel="Delete"
        onConfirm={() => {
          setConfirmDeleteOpen(false);
          remove.mutate();
        }}
        onCancel={() => setConfirmDeleteOpen(false)}
      />

      {remove.isError && (
        <p role="alert" className="form__error">
          {parseApiError(remove.error).message}
        </p>
      )}

      <dl>
        <dt>Status</dt>
        <dd>{invoice.status}</dd>
        <dt>Tenant</dt>
        <dd>{invoice.tenant_name}</dd>
        <dt>Unit</dt>
        <dd>{invoice.unit_number}</dd>
        <dt>Property</dt>
        <dd>{invoice.property_name}</dd>
        <dt>Charge type</dt>
        <dd>{invoice.charge_type}</dd>
        <dt>Amount due</dt>
        <dd>{invoice.amount_due}</dd>
        <dt>Amount paid</dt>
        <dd>{invoice.amount_paid}</dd>
        <dt>Balance</dt>
        <dd>{invoice.balance}</dd>
        <dt>Due date</dt>
        <dd>{invoice.due_date}</dd>
        <dt>Billing period</dt>
        <dd>{invoice.billing_period}</dd>
        <dt>Notes</dt>
        <dd>{invoice.notes === '' ? '—' : invoice.notes}</dd>
      </dl>

      {canRecordPayment && (
        <>
          <h3>Record payment</h3>
          <form onSubmit={handleRecordPayment} noValidate>
            {paymentError !== null && (
              <p role="alert" className="form__error">
                {paymentError}
              </p>
            )}

            <label htmlFor="payment-amount">Amount</label>
            <input
              id="payment-amount"
              required
              value={paymentForm.amount}
              onChange={(event) =>
                setPaymentForm((previous) => ({ ...previous, amount: event.target.value }))
              }
            />

            <label htmlFor="payment-method">Method</label>
            <select
              id="payment-method"
              value={paymentForm.method}
              onChange={(event) =>
                setPaymentForm((previous) => ({
                  ...previous,
                  method: event.target.value as PaymentMethod,
                }))
              }
            >
              {PAYMENT_METHODS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>

            <label htmlFor="payment-reference">Reference</label>
            <input
              id="payment-reference"
              required
              value={paymentForm.reference}
              onChange={(event) =>
                setPaymentForm((previous) => ({ ...previous, reference: event.target.value }))
              }
            />

            <button type="submit" disabled={recordPayment.isPending}>
              {recordPayment.isPending ? 'Recording…' : 'Record payment'}
            </button>
          </form>
        </>
      )}

      {invoice.status !== 'PAID' && (
        <section aria-label="Pay with M-Pesa">
          <h3>Pay with M-Pesa</h3>

          <p>
            <button
              type="button"
              aria-pressed={mpesaTab === 'STK'}
              onClick={() => setMpesaTab('STK')}
            >
              STK Push
            </button>{' '}
            <button type="button" aria-pressed={mpesaTab === 'QR'} onClick={() => setMpesaTab('QR')}>
              QR Code
            </button>
          </p>

          {mpesaTab === 'STK' && (
            <div>
              <form onSubmit={handleStkSubmit} noValidate>
                {stkPush.isError && (
                  <p role="alert" className="form__error">
                    {parseApiError(stkPush.error).message}
                  </p>
                )}

                <label htmlFor="mpesa-phone">Phone number</label>
                <input
                  id="mpesa-phone"
                  type="tel"
                  required
                  placeholder="0712345678"
                  value={phoneNumber}
                  onChange={(event) => setPhoneNumber(event.target.value)}
                />

                <button type="submit" disabled={stkPush.isPending}>
                  {stkPush.isPending ? 'Sending…' : 'Send payment request'}
                </button>
              </form>

              {checkoutRequestId !== null && (
                <p role="status">
                  {stkStatus.data === undefined && 'Checking payment status…'}
                  {stkStatus.data?.status === 'PENDING' &&
                    'Payment request sent — check your phone to complete it…'}
                  {stkStatus.data?.status === 'SUCCESS' && 'Payment successful.'}
                  {stkStatus.data?.status === 'FAILED' && 'Payment failed or was cancelled.'}
                </p>
              )}
            </div>
          )}

          {mpesaTab === 'QR' && (
            <div>
              {qrGeneration.isError && (
                <p role="alert" className="form__error">
                  {parseApiError(qrGeneration.error).message}
                </p>
              )}

              <button
                type="button"
                onClick={() => qrGeneration.mutate()}
                disabled={qrGeneration.isPending}
              >
                {qrGeneration.isPending ? 'Generating…' : 'Generate QR'}
              </button>

              {qrGeneration.data && (
                <>
                  <p>
                    <img
                      src={`data:image/png;base64,${qrGeneration.data.qr_code}`}
                      alt="M-Pesa payment QR code"
                    />
                  </p>
                  <p>
                    <small>
                      Scan with the M-Pesa app to pay. Refresh this page once you've completed the
                      payment.
                    </small>
                  </p>
                </>
              )}
            </div>
          )}
        </section>
      )}
    </section>
  );
}

export default InvoiceDetailPage;
